import React, { useState, useEffect } from 'react';
import { ProjectStatus, Country } from '../types';
import type { Project } from '../types';
import { Edit, Trash2, Eye, MapPin, Truck, ChevronRight, ChevronDown, ChevronUp, Columns, Check, Calendar, DollarSign, User, FileText, Activity, Layout, RefreshCcw, Settings2, Globe } from 'lucide-react';

interface ProjectTableProps {
  projects: Project[];
  onEdit: (project: Project) => void;
  onDelete: (id: string) => void;
  onView: (project: Project) => void;
  canDelete?: boolean;
  canEdit?: boolean;
  isGlobalMode?: boolean;
}

const STORAGE_KEY = 'nexus_project_table_columns_v3';

const DEFAULT_COLUMNS = {
  country: true,
  region: true,
  kilometers: true,
  status: true,
  contractor: true,
  financer: false,
  consultant: false,
  progress: true,
  dates: false,
  tender: false
};

export const ProjectTable: React.FC<ProjectTableProps> = ({ projects, onEdit, onDelete, onView, canDelete = false, canEdit = false, isGlobalMode = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  // Initialize from localStorage or defaults
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
        try {
            return { ...DEFAULT_COLUMNS, ...JSON.parse(saved) };
        } catch (e) {
            return DEFAULT_COLUMNS;
        }
    }
    return DEFAULT_COLUMNS;
  });

  // Save to localStorage whenever visibleColumns changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const columnLabels: Record<string, string> = {
    country: 'Branch',
    region: 'Region',
    kilometers: 'Distance',
    status: 'Status',
    contractor: 'Contractor',
    financer: 'Financer',
    consultant: 'Consultant',
    progress: 'Progress',
    dates: 'Project Dates',
    tender: 'Tender Number'
  };

  const toggleColumn = (key: string) => {
    setVisibleColumns(prev => ({
      ...prev,
      [key as keyof typeof visibleColumns]: !prev[key as keyof typeof visibleColumns]
    }));
  };

  const resetToDefault = () => {
    setVisibleColumns(DEFAULT_COLUMNS);
  };

  const toggleRowExpansion = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.COMPLETED: return 'bg-green-50 text-green-700 border-green-200';
      case ProjectStatus.IN_PROGRESS: return 'bg-blue-50 text-blue-700 border-blue-200';
      case ProjectStatus.PLANNING: return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case ProjectStatus.UNDER_TENDERING: return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case ProjectStatus.PAUSED: return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCountryColor = (country?: Country) => {
    switch(country) {
      case Country.TANZANIA: return 'bg-emerald-500';
      case Country.UGANDA: return 'bg-amber-500';
      case Country.KENYA: return 'bg-red-600';
      default: return 'bg-slate-400';
    }
  };

  return (
    <div className="bg-white rounded-[40px] shadow-sm border border-gray-200 flex flex-col relative overflow-hidden transition-all duration-300">
      
      {/* Table Toolbar */}
      <div className="px-8 py-5 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-20 shadow-sm">
        <div className="text-sm font-bold text-gray-800 flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-400 border border-indigo-100 shadow-inner">
              <Layout size={18} />
            </div>
            <div>
                <span className="block font-black uppercase tracking-tight">Infrastructure Ledger</span>
                <span className="text-[10px] text-slate-400 uppercase tracking-widest">{projects.length} Entries found in query</span>
            </div>
        </div>
        
        {/* Column Toggler */}
        <div className="relative">
            <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`flex items-center space-x-2 text-[11px] font-black uppercase tracking-widest px-6 py-3 rounded-2xl border transition-all active:scale-95 ${isMenuOpen ? 'bg-indigo-600 text-white border-indigo-600 shadow-xl shadow-indigo-100' : 'text-slate-600 bg-white hover:bg-slate-50 border-slate-200 shadow-sm'}`}
            >
                <Settings2 size={14} />
                <span>Registry Layout</span>
                <ChevronDown size={14} className={`transition-transform duration-300 ${isMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {isMenuOpen && (
                <>
                <div className="fixed inset-0 z-10" onClick={() => setIsMenuOpen(false)}></div>
                <div className="absolute right-0 mt-3 w-72 bg-white rounded-[32px] shadow-2xl border border-slate-100 z-20 py-3 animate-in fade-in slide-in-from-top-4 overflow-hidden ring-1 ring-black ring-opacity-5">
                    <div className="px-6 py-4 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Table Configuration</span>
                        <button 
                            onClick={resetToDefault}
                            className="text-[10px] font-black text-indigo-600 hover:text-indigo-800 uppercase flex items-center gap-1.5 transition-colors"
                        >
                            <RefreshCcw size={12} /> Reset
                        </button>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto py-2 custom-scrollbar">
                        {Object.keys(columnLabels).map(key => {
                            const isVisible = visibleColumns[key as keyof typeof visibleColumns];
                            return (
                                <button
                                key={key}
                                onClick={() => toggleColumn(key)}
                                className={`w-full text-left px-6 py-3 text-sm flex items-center justify-between transition-all duration-200 group
                                    ${isVisible 
                                    ? 'text-indigo-700 bg-indigo-50/20' 
                                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                    }`}
                                >
                                <span className="flex items-center">
                                    <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center mr-3 transition-all ${isVisible ? 'bg-indigo-600 border-indigo-600 shadow-md scale-110' : 'border-slate-200 bg-white group-hover:border-slate-300'}`}>
                                        {isVisible && <Check size={12} className="text-white" strokeWidth={4} />}
                                    </div>
                                    <span className={`font-bold transition-all ${isVisible ? 'translate-x-1' : ''}`}>{columnLabels[key]}</span>
                                </span>
                                </button>
                            );
                        })}
                    </div>
                    <div className="px-6 py-3 border-t border-slate-50 bg-slate-50/30 text-[9px] text-slate-400 text-center font-bold uppercase tracking-widest">
                        Configuration stored locally
                    </div>
                </div>
                </>
            )}
        </div>
      </div>

      <div className="overflow-x-auto custom-scrollbar">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50/80 backdrop-blur-sm hidden md:table-header-group">
            <tr className="text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
              <th scope="col" className="px-8 py-5 sticky left-0 bg-slate-50 z-10">Project Identity</th>
              {visibleColumns.country && <th scope="col" className="px-8 py-5">Node</th>}
              {visibleColumns.region && <th scope="col" className="px-8 py-5">Region</th>}
              {visibleColumns.kilometers && <th scope="col" className="px-8 py-5">Distance</th>}
              {visibleColumns.status && <th scope="col" className="px-8 py-5">Status</th>}
              {visibleColumns.contractor && <th scope="col" className="px-8 py-5">Contractor</th>}
              {visibleColumns.financer && <th scope="col" className="px-8 py-5">Financer</th>}
              {visibleColumns.consultant && <th scope="col" className="px-8 py-5">Consultant</th>}
              {visibleColumns.dates && <th scope="col" className="px-8 py-5">Timeline</th>}
              {visibleColumns.tender && <th scope="col" className="px-8 py-5">Tender #</th>}
              {visibleColumns.progress && <th scope="col" className="px-8 py-5">Progress</th>}
              <th scope="col" className="px-8 py-5 text-right sticky right-0 bg-slate-50 z-10">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-50">
            {projects.map((project) => {
              const isExpanded = expandedRows.has(project.id);
              return (
                <tr 
                  key={project.id} 
                  onClick={() => onView(project)}
                  className="hover:bg-indigo-50/10 transition-all cursor-pointer group flex flex-col md:table-row border-b md:border-none p-6 md:p-0"
                >
                  {/* PROJECT IDENTITY CELL */}
                  <td className="md:px-8 md:py-7 md:sticky md:left-0 md:bg-white md:group-hover:bg-slate-50 transition-colors md:z-10 md:shadow-[4px_0_12px_-4px_rgba(0,0,0,0.05)] w-full md:w-auto">
                    <div className="flex flex-col">
                      <span className="text-base md:text-sm font-black text-slate-900 mb-1 leading-tight group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{project.name}</span>
                      
                      {/* MOBILE REVEAL DETAILS */}
                      <div className="md:hidden mt-4 space-y-4">
                          <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${getCountryColor(project.country)}`}></div>
                                <span className="text-[10px] font-black uppercase text-slate-500">{project.country}</span>
                              </div>
                              <span className={`px-2.5 py-0.5 inline-flex text-[10px] font-black uppercase rounded-full border ${getStatusColor(project.status)}`}>
                                  {project.status}
                              </span>
                          </div>

                          {isExpanded && (
                              <div className="pt-4 border-t border-slate-100 grid grid-cols-2 gap-y-4 gap-x-2 text-xs text-slate-600 animate-in fade-in slide-in-from-top-2">
                                  <div className="flex flex-col gap-1">
                                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Region</span>
                                      <span className="font-bold flex items-center gap-1.5"><MapPin size={12} className="text-indigo-400" />{project.region}</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Contractor</span>
                                      <span className="font-bold flex items-center gap-1.5"><Truck size={12} className="text-indigo-400" />{project.contractor}</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Distance</span>
                                      <span className="font-bold flex items-center gap-1.5"><Activity size={12} className="text-indigo-400" />{project.kilometers} km</span>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Funding</span>
                                      <span className="font-bold flex items-center gap-1.5"><DollarSign size={12} className="text-indigo-400" />{project.financer || 'N/A'}</span>
                                  </div>
                              </div>
                          )}

                          <button 
                            onClick={(e) => toggleRowExpansion(project.id, e)}
                            className="w-full py-2 bg-slate-50 rounded-xl flex items-center justify-center text-[10px] font-black uppercase text-slate-400 hover:text-indigo-600 transition-all tracking-[0.2em] mt-2 border border-slate-100"
                          >
                            {isExpanded ? 'Collapse Insights' : 'View Full Details'}
                            {isExpanded ? <ChevronUp size={14} className="ml-2" /> : <ChevronDown size={14} className="ml-2" />}
                          </button>
                      </div>
                    </div>
                  </td>

                  {/* COUNTRY CELL */}
                  {visibleColumns.country && (
                      <td className="hidden md:table-cell px-8 py-6 whitespace-nowrap">
                         <div className="flex items-center gap-2">
                             <div className={`w-2 h-2 rounded-full ${getCountryColor(project.country)} shadow-sm`}></div>
                             <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{project.country?.substring(0, 3)} Node</span>
                         </div>
                      </td>
                  )}
                  
                  {/* DYNAMIC DESKTOP COLUMNS */}
                  {visibleColumns.region && (
                    <td className="hidden md:table-cell px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center text-sm font-bold text-slate-700">
                          <MapPin size={14} className="mr-2 text-indigo-400" />
                          {project.region}
                      </div>
                    </td>
                  )}
                  
                  {visibleColumns.kilometers && (
                    <td className="hidden md:table-cell px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center text-sm font-black text-slate-800 font-mono">
                          {project.kilometers.toFixed(1)} <span className="text-[9px] text-slate-400 ml-1">KM</span>
                      </div>
                    </td>
                  )}

                  {visibleColumns.status && (
                    <td className="hidden md:table-cell px-8 py-6 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-[10px] font-black uppercase leading-4 rounded-full border shadow-sm ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </td>
                  )}

                  {visibleColumns.contractor && (
                    <td className="hidden md:table-cell px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center text-sm font-bold text-slate-600 truncate max-w-[180px]" title={project.contractor}>
                        <Truck size={14} className="mr-2 text-slate-300 flex-shrink-0" />
                        <span className="truncate">{project.contractor}</span>
                      </div>
                    </td>
                  )}

                  {visibleColumns.financer && (
                     <td className="hidden md:table-cell px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center text-sm font-bold text-slate-600">
                        <DollarSign size={14} className="mr-2 text-emerald-400" />
                        {project.financer || 'N/A'}
                      </div>
                    </td>
                  )}

                  {visibleColumns.consultant && (
                     <td className="hidden md:table-cell px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center text-sm font-bold text-slate-600">
                        <User size={14} className="mr-2 text-indigo-400" />
                        <span className="truncate max-w-[150px]">{project.consultant || 'N/A'}</span>
                      </div>
                    </td>
                  )}

                  {visibleColumns.dates && (
                     <td className="hidden md:table-cell px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="flex items-center text-[10px] font-black text-slate-700 uppercase tracking-tighter">
                          <Calendar size={12} className="mr-1.5 text-slate-300" />
                          Start: {project.startDate}
                        </div>
                        <div className="flex items-center text-[10px] font-bold text-slate-400 uppercase tracking-tighter mt-1">
                          <div className="w-3 mr-1.5"></div>
                          End: {project.estimatedCompletion || 'TBD'}
                        </div>
                      </div>
                    </td>
                  )}

                  {visibleColumns.tender && (
                     <td className="hidden md:table-cell px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center text-xs font-black text-indigo-600 font-mono bg-indigo-50/50 px-2 py-1 rounded-lg border border-indigo-100/50 w-fit">
                        <FileText size={12} className="mr-1.5 opacity-50" />
                        {project.tenderNumber || 'NO-REF'}
                      </div>
                    </td>
                  )}

                  {visibleColumns.progress && (
                    <td className="hidden md:table-cell px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 w-20 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-1000 ease-out ${project.progress === 100 ? 'bg-green-500' : 'bg-indigo-600'}`} 
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-[10px] font-black text-slate-700">{project.progress}%</span>
                      </div>
                    </td>
                  )}

                  {/* ACTIONS COLUMN */}
                  <td className="md:px-8 md:py-6 whitespace-nowrap text-right md:sticky md:right-0 md:bg-white md:group-hover:bg-slate-50 transition-colors md:z-10 md:shadow-[-4px_0_12px_-4px_rgba(0,0,0,0.05)] absolute right-4 top-4 md:static">
                    <div className="flex items-center justify-end gap-1">
                      <button 
                        onClick={(e) => { e.stopPropagation(); onView(project); }}
                        className="p-2.5 text-slate-400 hover:text-indigo-600 hover:bg-white hover:shadow-sm rounded-xl transition-all"
                        title="Audit Registry"
                      >
                        <Eye size={18} />
                      </button>
                      {canEdit && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); onEdit(project); }}
                          className="p-2.5 text-slate-400 hover:text-blue-600 hover:bg-white hover:shadow-sm rounded-xl transition-all"
                          title="Modify Entry"
                        >
                          <Edit size={18} />
                        </button>
                      )}
                      {canDelete && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); onDelete(project.id); }}
                          className="p-2.5 text-slate-400 hover:text-rose-600 hover:bg-white hover:shadow-sm rounded-xl transition-all"
                          title="Revoke Entry"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {projects.length === 0 && (
          <div className="py-40 flex flex-col items-center justify-center text-center px-6">
              <div className="w-24 h-24 bg-slate-50 rounded-[40px] flex items-center justify-center text-slate-200 mb-6 border border-slate-100 shadow-inner">
                <FileText size={48} />
              </div>
              <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter">Registry Empty</h3>
              <p className="text-slate-400 text-sm mt-2 max-w-xs leading-relaxed font-medium">No logistical infrastructure matching current filters was detected in the {isGlobalMode ? 'global' : 'branch'} ledger.</p>
              <button 
                onClick={() => window.location.reload()}
                className="mt-8 text-indigo-600 font-black text-[10px] uppercase tracking-widest hover:underline flex items-center gap-2"
              >
                <RefreshCcw size={12} /> Sync Global Node
              </button>
          </div>
      )}
    </div>
  );
};